import { MESSAGES } from '@aibees/elephant/constants';
import { AddListToInputCheckbox } from '@aibees/elephant/feature/search-table-people/components/add-list-to-input-checkbox';
import { useBootstrapLabels } from '@aibees/elephant/hooks/bootstrap/use-bootstrap-labels';
import { useLabelTypeFromRoute } from '@aibees/elephant/hooks/labels/use-label-type-from-route';
import { Label, LabelVisibility } from '@aibees/elephant/types/label';
import { CardBox, Grid2, Icon, Scrollbar } from '@aibees/shared/components';
import { classnames, searchArrayOfObjects } from '@aibees/shared/utils';
import { TabContext, TabList } from '@mui/lab';
import {
  Button,
  ButtonProps,
  CircularProgress,
  Stack,
  Tab,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { sortBy } from 'lodash-es';
import React, { memo, useEffect, useState } from 'react';
import { useLabelManagementContext } from '../search-table-action-dialog-lists/context';
import * as Styled from './styles';
import * as SX from './sx';
import { type RecordLabelManagerProps as Props } from './types';

export const RecordLabelManager = memo(
  ({
    className,
    title,
    type,
    state,
    currentLabels,
    loading,
    onSubmit,
    onClose,
  }: Props) => {
    const { labels, justCreatedLabels } = useLabelManagementContext();
    const labelType = useLabelTypeFromRoute();

    const [tabValue, setTabValue] = useState(LabelVisibility.ALL);
    const [checkedItems, setCheckedItems] =
      useState<Label[]>(justCreatedLabels);
    const [uncheckedItems, setUncheckedItems] = useState<Label[]>([]);
    const [searchText, setSearchText] = useState('');

    const relevantCheckedItems = checkedItems.filter(
      (item) => item.type === type
    ); // TODO is it needed to check the label type?
    const disabled = relevantCheckedItems.length === 0;
    const data = useBootstrapLabels(type, labelType, tabValue);
    const searchResults = searchArrayOfObjects(
      data.items as never[],
      'name',
      searchText
    );

    const items = sortBy(
      searchText ? searchResults : data.items,
      (item) => checkedItems.map((x) => x.id).indexOf(item.id) < 0
    ) as Label[];

    const handleChange = (checked: boolean, label: Label) => {
      setCheckedItems((prevChecked) =>
        checked
          ? [label, ...prevChecked]
          : prevChecked.filter((i) => i.id !== label.id)
      );

      setUncheckedItems((prevUnchecked) =>
        checked
          ? prevUnchecked.filter((i) => i.id !== label.id)
          : [...prevUnchecked, label]
      );
    };

    const handleTabChange = (
      _: React.SyntheticEvent,
      newValue: LabelVisibility
    ) => setTabValue(newValue);

    const handleSearch: TextFieldProps['onChange'] = (e) => {
      setSearchText(e.target.value);
    };

    const handleSubmit: ButtonProps['onClick'] = () => {
      onSubmit?.(checkedItems, uncheckedItems);
    };

    const handleClearAll = () => setCheckedItems([]);

    const handleSelectAll = () => setCheckedItems(items);

    useEffect(() => {
      setCheckedItems([
        ...(labels ?? []),
        ...checkedItems,
        ...(currentLabels ?? []),
        ...justCreatedLabels,
      ]);
    }, [labels, justCreatedLabels]);

    return (
      <Styled.Wrapper
        className={classnames('RecordLabelManager', className)}
        sx={SX.root}
        contentSx={SX.content}
      >
        {title && <Typography sx={SX.title}>{title}</Typography>}

        <Styled.Input
          name="name"
          placeholder="Search List"
          size="small"
          InputProps={{
            startAdornment: (
              <Icon name="search-fill" sxx={SX.searchIcon} size={14} />
            ),
          }}
          value={searchText}
          onChange={handleSearch}
          autoComplete="off"
        />

        <CardBox contentSx={SX.cardBox}>
          <TabContext value={tabValue}>
            <TabList onChange={handleTabChange} variant="fullWidth">
              {Object.values(LabelVisibility).map((visibility) => (
                <Tab
                  key={visibility}
                  label={
                    <Typography sx={SX.tabTitle}>
                      {MESSAGES[visibility]}
                    </Typography>
                  }
                  value={visibility}
                />
              ))}
            </TabList>
          </TabContext>
          <Scrollbar sx={{ height: (p) => p.spacing(20) }}>
            {items?.map((i) => (
              <AddListToInputCheckbox
                key={i.id}
                item={i}
                items={items}
                checked={checkedItems.some(
                  (checkedItem) => checkedItem.id === i.id
                )}
                disabled={currentLabels?.some(
                  (checkedItem) => checkedItem.id === i.id
                )}
                onChange={handleChange}
              />
            ))}
          </Scrollbar>
        </CardBox>
        <Stack direction="row" justifyContent="space-between">
          <Grid2>
            <Button
              sx={SX.cancelButton}
              variant="text"
              onClick={handleClearAll}
              disabled={!checkedItems.length}
            >
              Clear All
            </Button>
            <Button
              sx={SX.cancelButton}
              variant="text"
              onClick={handleSelectAll}
            >
              Select All
            </Button>
          </Grid2>

          <Grid2>
            <Button variant="text" sx={SX.cancelButton} onClick={onClose}>
              Cancel
            </Button>

            <Button
              sx={SX.applyButton}
              color="primary"
              disabled={disabled || loading || data.isLoading}
              onClick={handleSubmit}
            >
              {loading || data.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                'Apply'
              )}
            </Button>
          </Grid2>
        </Stack>
      </Styled.Wrapper>
    );
  }
);
RecordLabelManager.displayName = 'RecordLabelManager';
